import React, { useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import { Container, CircularProgress, Alert, Box, Tabs, Tab, Paper } from "@mui/material";
import { api } from "../api";
import { useAuth } from "../auth/AuthContext";

export default function Docs() {
  const { isAnyAdmin, isSuperAdmin } = useAuth();
  // RBAC-scoped: the Admin Guide tab only exists for admins, and the backend
  // /api/docs/admin-guide endpoint independently enforces admin access.
  const docs = useMemo(
    () => [
      { label: "User Guide", name: "user-guide" },
      ...(isAnyAdmin ? [{ label: "Admin Guide", name: "admin-guide" }] : []),
      ...(isSuperAdmin ? [{ label: "Super-Admin Guide", name: "super-admin-guide" }] : []),
    ],
    [isAnyAdmin, isSuperAdmin]
  );

  const [tab, setTab] = useState(0);
  const [md, setMd] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const active = Math.min(tab, docs.length - 1);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    (async () => {
      try {
        const { data } = await api.get<{ markdown: string }>(`/docs/${docs[active].name}`);
        if (!cancelled) setMd((data.markdown || "").replace(/\{\{APP_URL\}\}/g, window.location.origin));
      } catch (e: any) {
        if (!cancelled) setErr(e?.message || "Failed to load docs");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [active, docs]);

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      {docs.length > 1 && (
        <Tabs
          value={active}
          onChange={(_, v) => setTab(v)}
          sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
        >
          {docs.map((d) => (
            <Tab key={d.name} label={d.label} />
          ))}
        </Tabs>
      )}

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      )}
      {err && <Alert severity="error">Error loading docs: {err}</Alert>}
      {!loading && !err && (
        <Paper
          variant="outlined"
          sx={{
            p: { xs: 2, md: 4 },
            lineHeight: 1.6,
            "& h1": { mt: 0 },
            "& h2": { mt: 4 },
            "& h3": { mt: 3 },
            "& code": { fontFamily: "monospace", fontSize: "0.85em" },
            "& pre": {
              p: 1.5,
              borderRadius: 1,
              overflowX: "auto",
              bgcolor: "rgba(0,0,0,0.35)",
              fontSize: "0.85em",
            },
            "& a": { color: "primary.main" },
            "& blockquote": { ml: 0, pl: 2, my: 2, fontStyle: "italic", opacity: 0.85 },
            "& ul, & ol": { pl: 3 },
            "& img": { maxWidth: "100%" },
          }}
        >
          <ReactMarkdown>{md}</ReactMarkdown>
        </Paper>
      )}
    </Container>
  );
}
